import { Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TextareaModule } from 'primeng/textarea';
import { CurrentContextService } from '../../core/context/current-context.service';
import { LiveUpdateService } from '../../core/live-updates/live-update.service';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinDatePipe } from '../../shared/design-system/ain-date.pipe';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { JalaliDateInputComponent } from '../../shared/design-system/jalali-date-input.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { MarketplaceStoreSelectorComponent } from '../../shared/design-system/marketplace-store-selector.component';
import { OfferingSelectorComponent } from '../../shared/design-system/offering-selector.component';
import { StructuredJsonFormComponent } from '../../shared/design-system/structured-json-form.component';
import { CatalogService, MarketplaceStore, OfferingAttributeAssignment, OfferingResponse, StorefrontPublication } from './catalog.service';
import {
  STOREFRONT_PUBLICATION_BADGE_FIELDS,
  STOREFRONT_PUBLICATION_SEO_FIELDS,
  createEmptyPublicationBadgePayload,
  normalizePublicationSeoJson,
  parsePublicationBadgesJson,
  serializePublicationBadgesJson
} from './storefront-publishing-schemas';
import {
  MARKETPLACE_LIST_TOOLBAR_STYLES,
  MarketplaceLiveSubscription,
  compareSortValues,
  downloadCsv,
  liveStatusKey,
  subscribeMarketplaceOperationsLiveUpdates,
  toggleSortField,
  unsubscribeMarketplaceOperationsLiveUpdates
} from './marketplace-list-page.utils';

type PublicationSortField = 'offeringId' | 'status' | 'displayOrder' | 'visibleFromUtc' | 'visibleToUtc';
type PublicationAction = 'publish' | 'unpublish' | 'archive';

@Component({
  standalone: true,
  selector: 'ain-storefront-publishing',
  imports: [
    AinPageComponent,
    AinStateComponent,
    InfoHintComponent,
    AinDatePipe,
    JalaliDateInputComponent,
    StructuredJsonFormComponent,
    MarketplaceStoreSelectorComponent,
    OfferingSelectorComponent,
    RouterLink,
    FormsModule,
    TableModule,
    ButtonModule,
    CheckboxModule,
    DialogModule,
    InputTextModule,
    SelectModule,
    TextareaModule
  ],
  template: `
    <ain-page [title]="t('catalog.storefront.publishing.title')" [description]="t('catalog.storefront.publishing.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'catalog.marketplace.help.list.title'"
        [descriptionKey]="'catalog.marketplace.help.list.description'"
        [bulletKeys]="[
          'catalog.marketplace.help.list.bullets.selection',
          'catalog.marketplace.help.list.bullets.filters',
          'catalog.marketplace.help.list.bullets.live'
        ]" />
      @if (loading()) {
        <ain-state type="loading" [title]="t('catalog.loading')" />
      } @else {
        <section class="panel">
          <div class="toolbar">
            <button pButton type="button" [label]="t('catalog.storefront.publishing.create')" (click)="openDialog()"></button>
            <button pButton type="button" severity="secondary" [label]="t('catalog.marketplace.actions.refresh')" (click)="load()"></button>
            <button pButton type="button" severity="secondary" [label]="t('catalog.marketplace.actions.bulk')" (click)="applyBulk()"></button>
            <button pButton type="button" severity="secondary" [label]="t('catalog.marketplace.actions.export')" (click)="exportRows()"></button>
            <button pButton type="button" severity="secondary" [label]="t('catalog.action.edit')" [disabled]="selectedRows().length !== 1" (click)="openSelected()"></button>
            <span class="live-status" [class]="liveUpdateService.connectionState()">
              {{ t(liveStatusKey(liveUpdateService.connectionState())) }}
            </span>
          </div>

          <details class="filters" [open]="true">
            <summary>
              <span>{{ t('catalog.marketplace.filters.title') }}</span>
              <ain-info-hint
                [titleKey]="'catalog.marketplace.help.filters.title'"
                [descriptionKey]="'catalog.marketplace.help.filters.description'"
                [bulletKeys]="[
                  'catalog.marketplace.help.filters.bullets.search',
                  'catalog.marketplace.help.filters.bullets.status'
                ]" />
            </summary>
            <div class="filter-grid">
              <label>
                <span>{{ t('catalog.marketplace.filters.store') }}</span>
                <ain-marketplace-store-selector
                  [(ngModel)]="selectedStoreId"
                  (ngModelChange)="onStoreChange()" />
              </label>
              <label>
                <span>{{ t('catalog.marketplace.filters.search') }}</span>
                <input pInputText [value]="search()" (input)="onSearch($event)" />
              </label>
              <label>
                <span>{{ t('catalog.marketplace.filters.status') }}</span>
                <p-select
                  [options]="statusOptions"
                  optionValue="value"
                  [(ngModel)]="statusFilter"
                  [showClear]="true"
                  appendTo="body">
                  <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : t('common.all') }}</ng-template>
                  <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
                </p-select>
              </label>
            </div>
          </details>

          @if (!selectedStoreId) {
            <ain-state type="empty" [title]="t('catalog.storefront.selectStore')" />
          } @else if (filteredRows().length === 0) {
            <ain-state type="empty" [title]="t('catalog.storefront.empty')" />
          } @else {
            <p-table
              [value]="filteredRows()"
              dataKey="id"
              selectionMode="multiple"
              [paginator]="true"
              [rows]="10"
              [selection]="selectedRows()"
              (selectionChange)="onSelectionChange($event)">
              <ng-template pTemplate="header">
                <tr>
                  <th style="width:44px"><p-tableHeaderCheckbox /></th>
                  <th>
                    <button type="button" class="sort-btn" [class.active]="sortField() === 'offeringId'" (click)="sortBy('offeringId')">
                      {{ t('catalog.storefront.publishing.offering') }}
                    </button>
                  </th>
                  <th>
                    <button type="button" class="sort-btn" [class.active]="sortField() === 'status'" (click)="sortBy('status')">
                      {{ t('columns.status') }}
                    </button>
                  </th>
                  <th>{{ t('catalog.storefront.publishing.visibleFrom') }} / {{ t('catalog.storefront.publishing.visibleTo') }}</th>
                  <th>
                    <button type="button" class="sort-btn" [class.active]="sortField() === 'displayOrder'" (click)="sortBy('displayOrder')">
                      {{ t('catalog.storefront.publishing.order') }}
                    </button>
                  </th>
                  <th></th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr>
                  <td><p-tableCheckbox [value]="row" /></td>
                  <td>
                    <button type="button" class="link-btn" (click)="openEdit(row)">{{ offeringLabel(row.offeringId) }}</button>
                  </td>
                  <td>{{ t(statusKey(row.status)) }}</td>
                  <td>{{ row.visibleFromUtc ? (row.visibleFromUtc | ainDate) : '—' }} / {{ row.visibleToUtc ? (row.visibleToUtc | ainDate) : '—' }}</td>
                  <td>{{ row.displayOrder }}</td>
                  <td class="actions">
                    <button pButton type="button" size="small" severity="secondary" [label]="t('catalog.action.edit')" [disabled]="acting()" (click)="openEdit(row)"></button>
                    <button pButton type="button" size="small" [label]="t('catalog.storefront.publishing.publish')" [disabled]="acting() || row.status === 'Published'" (click)="openActionDialog('publish', row.id)"></button>
                    <button pButton type="button" size="small" severity="secondary" [label]="t('catalog.storefront.publishing.unpublish')" [disabled]="acting()" (click)="openActionDialog('unpublish', row.id)"></button>
                    <button pButton type="button" size="small" severity="danger" [label]="t('catalog.storefront.publishing.archive')" [disabled]="acting()" (click)="openActionDialog('archive', row.id)"></button>
                  </td>
                </tr>
              </ng-template>
            </p-table>
          }

          @if (info()) { <p class="info">{{ info() }}</p> }
        </section>
      }

      <p-dialog [header]="dialogTitle()" [(visible)]="dialogVisible" [modal]="true" [style]="{ width: '640px' }">
        <div class="form">
          <label>
            <span>{{ t('catalog.marketplace.filters.store') }}</span>
            @if (isEditMode()) {
              <p class="readonly-value">{{ storeLabel(dialogStoreId ?? '') }}</p>
            } @else {
              <ain-marketplace-store-selector [(ngModel)]="dialogStoreId" class="full" />
            }
          </label>
          <label>
            <span>{{ t('catalog.storefront.publishing.offering') }}</span>
            @if (isEditMode()) {
              <p class="readonly-value">{{ offeringLabel(selectedOfferingId ?? '') }}</p>
            } @else {
              <ain-offering-selector [(ngModel)]="selectedOfferingId" class="full" (ngModelChange)="onOfferingSelectionChange($event)" />
            }
          </label>

          @if (selectedOfferingId) {
            <section class="form-section attribute-summary">
              <div class="section-head">
                <h4>{{ t('catalog.storefront.publishing.attributes.section') }}</h4>
                <a [routerLink]="['/catalog/offerings', selectedOfferingId]" [queryParams]="{ tab: 'attributes' }" class="manage-link">
                  {{ t('catalog.storefront.publishing.attributes.manageLink') }}
                </a>
              </div>
              <ain-info-hint
                [titleKey]="'catalog.storefront.publishing.attributes.hint.title'"
                [descriptionKey]="'catalog.storefront.publishing.attributes.hint.description'" />
              @if (attributesLoading()) {
                <p class="section-hint">{{ t('common.loading') }}</p>
              } @else if (offeringAttributes().length === 0) {
                <p class="section-hint">{{ t('catalog.storefront.publishing.attributes.empty') }}</p>
              } @else {
                @if (capabilityAttributes().length > 0) {
                  <div class="summary-block">
                    <strong>{{ t('catalog.storefront.publishing.attributes.capabilities') }}</strong>
                    <div class="tag-row">
                      @for (item of capabilityAttributes(); track item.id) {
                        <span class="summary-tag" [class.enabled]="isTruthyAttribute(item)">{{ item.displayName || item.key }}</span>
                      }
                    </div>
                  </div>
                }
                @if (metadataAttributes().length > 0) {
                  <div class="summary-block">
                    <strong>{{ t('catalog.storefront.publishing.attributes.metadata') }}</strong>
                    <ul class="summary-list">
                      @for (item of metadataAttributes(); track item.id) {
                        <li><span>{{ item.displayName || item.key }}</span><em>{{ formatAttributeSummaryValue(item) }}</em></li>
                      }
                    </ul>
                  </div>
                }
              }
            </section>
          }
          @if (isEditMode()) {
            <p class="status-chip">{{ t('columns.status') }}: {{ t(statusKey(editingStatus)) }}</p>
          }
          <label>
            <span>{{ t('catalog.storefront.publishing.order') }}</span>
            <input pInputText type="number" [(ngModel)]="displayOrder" class="full" />
          </label>
          <label>
            <span>{{ t('catalog.storefront.publishing.visibleFrom') }}</span>
            <ain-date-input [(ngModel)]="visibleFromUtc" class="full" />
          </label>
          <label>
            <span>{{ t('catalog.storefront.publishing.visibleTo') }}</span>
            <ain-date-input [(ngModel)]="visibleToUtc" class="full" />
          </label>
          <label class="checkbox-row">
            <p-checkbox [(ngModel)]="isPlatformDirectSaleAllowed" [binary]="true" />
            <span>{{ t('catalog.storefront.publishing.platformDirectSale') }}</span>
          </label>
          <p class="section-hint">{{ t('catalog.storefront.publishing.platformDirectSaleHint') }}</p>

          <section class="form-section">
            <div class="section-head">
              <h4>{{ t('catalog.storefront.publishing.badges.section') }}</h4>
              <button pButton type="button" size="small" severity="secondary" [label]="t('catalog.storefront.publishing.badges.add')" (click)="addBadge()"></button>
            </div>
            @if (!badgePayloads.length) {
              <p class="section-hint">{{ t('catalog.storefront.publishing.badges') }}</p>
            } @else {
              @for (payload of badgePayloads; track $index) {
                <article class="badge-card">
                  <div class="badge-card-head">
                    <strong>{{ t('catalog.storefront.publishing.badges') }} {{ $index + 1 }}</strong>
                    <button pButton type="button" size="small" severity="danger" [text]="true" [label]="t('catalog.storefront.publishing.badges.remove')" (click)="removeBadge($index)"></button>
                  </div>
                  <ain-structured-json-form [fields]="badgeFields" [(payloadJson)]="badgePayloads[$index]" />
                </article>
              }
            }
          </section>

          <section class="form-section">
            <h4>{{ t('catalog.storefront.publishing.seo.section') }}</h4>
            <ain-structured-json-form [fields]="seoFields" [(payloadJson)]="seoOverrideJson" />
          </section>

          @if (dialogError()) { <p class="error">{{ dialogError() }}</p> }
        </div>
        <ng-template pTemplate="footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="closeDialog()"></button>
          <button pButton type="button" [label]="dialogSaveLabel()" [disabled]="acting()" (click)="save()"></button>
        </ng-template>
      </p-dialog>

      <p-dialog [header]="actionDialogTitle()" [(visible)]="actionDialogVisible" [modal]="true" [style]="{ width: '420px' }">
        <div class="form">
          <label>{{ t('catalog.marketplace.storefront.confirm.reason') }}</label>
          <textarea pTextarea rows="3" [(ngModel)]="actionReason" class="full"></textarea>
          @if (actionError()) { <p class="error">{{ actionError() }}</p> }
        </div>
        <ng-template pTemplate="footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="closeActionDialog()"></button>
          <button pButton type="button" [label]="actionConfirmLabel()" [disabled]="acting()" (click)="confirmAction()"></button>
        </ng-template>
      </p-dialog>
    </ain-page>
  `,
  styles: [`
    .error { margin: 0; color: #b91c1c; }
    .form label > span { color: #475569; font-size: 12px; font-weight: 600; }
    .form-section { display: grid; gap: 10px; padding-top: 4px; border-top: 1px solid #e2e8f0; }
    .form-section h4 { margin: 0; font-size: 14px; }
    .section-head { display: flex; justify-content: space-between; align-items: center; gap: 8px; }
    .section-hint { margin: 0; color: #64748b; font-size: 13px; }
    .badge-card { border: 1px solid #e2e8f0; border-radius: 10px; padding: 10px; display: grid; gap: 8px; background: #f8fafc; }
    .badge-card-head { display: flex; justify-content: space-between; align-items: center; gap: 8px; }
    .checkbox-row { display: flex; align-items: center; gap: 8px; }
    .link-btn { border: 0; background: none; color: #1d4ed8; cursor: pointer; font-weight: 600; padding: 0; text-align: left; }
    .readonly-value { margin: 0; padding: 8px 10px; border: 1px solid #e2e8f0; border-radius: 8px; background: #f8fafc; font-size: 14px; }
    .status-chip { margin: 0; color: #475569; font-size: 13px; font-weight: 600; }
    .attribute-summary .manage-link { color: #1d4ed8; font-weight: 600; text-decoration: none; font-size: 13px; }
    .summary-block { display: grid; gap: 8px; }
    .summary-block strong { font-size: 13px; color: #334155; }
    .tag-row { display: flex; flex-wrap: wrap; gap: 6px; }
    .summary-tag { border: 1px solid #e2e8f0; border-radius: 999px; padding: 2px 10px; font-size: 12px; color: #64748b; background: #f8fafc; }
    .summary-tag.enabled { border-color: #86efac; color: #166534; background: #ecfdf5; }
    .summary-list { list-style: none; margin: 0; padding: 0; display: grid; gap: 6px; }
    .summary-list li { display: flex; justify-content: space-between; gap: 12px; font-size: 13px; }
    .summary-list span { color: #475569; }
    .summary-list em { font-style: normal; color: #0f172a; font-weight: 600; text-align: right; }
    .actions { display: flex; flex-wrap: wrap; gap: 6px; }
    ${MARKETPLACE_LIST_TOOLBAR_STYLES}
  `]
})
export class StorefrontPublishingComponent implements OnInit, OnDestroy {
  private readonly service = inject(CatalogService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly currentContext = inject(CurrentContextService);
  readonly liveUpdateService = inject(LiveUpdateService);
  private readonly localization = inject(LocalizationService);

  private liveSubscription: MarketplaceLiveSubscription = { topic: null, eventsSubscription: null };

  readonly loading = signal(true);
  readonly acting = signal(false);
  readonly info = signal('');
  readonly actionError = signal('');
  readonly dialogError = signal('');
  readonly offerings = signal<OfferingResponse[]>([]);
  readonly stores = signal<MarketplaceStore[]>([]);
  readonly rows = signal<StorefrontPublication[]>([]);
  readonly selectedRows = signal<StorefrontPublication[]>([]);
  readonly search = signal('');
  readonly sortField = signal<PublicationSortField>('displayOrder');
  readonly sortOrder = signal<1 | -1>(1);
  readonly editingPublicationId = signal<string | null>(null);
  readonly offeringAttributes = signal<OfferingAttributeAssignment[]>([]);
  readonly attributesLoading = signal(false);

  readonly capabilityAttributes = () =>
    this.offeringAttributes().filter(item => item.key.startsWith('capability_'));

  readonly metadataAttributes = () =>
    this.offeringAttributes().filter(item => !item.key.startsWith('capability_'));

  readonly badgeFields = STOREFRONT_PUBLICATION_BADGE_FIELDS;
  readonly seoFields = STOREFRONT_PUBLICATION_SEO_FIELDS;
  badgePayloads: string[] = [];
  liveStatusKey = liveStatusKey;

  readonly statusOptions = [
    { value: 'Draft', labelKey: 'catalog.marketplace.storefront.status.draft' },
    { value: 'Published', labelKey: 'catalog.marketplace.storefront.status.published' },
    { value: 'Unpublished', labelKey: 'catalog.marketplace.storefront.status.unpublished' },
    { value: 'Archived', labelKey: 'catalog.marketplace.storefront.status.archived' }
  ];

  selectedStoreId: string | null = null;
  dialogStoreId: string | null = null;
  statusFilter: string | null = null;
  dialogVisible = false;
  selectedOfferingId: string | null = null;
  displayOrder = 10;
  visibleFromUtc: string | null = null;
  visibleToUtc: string | null = null;
  isPlatformDirectSaleAllowed = false;
  seoOverrideJson = '{}';

  actionDialogVisible = false;
  actionType: PublicationAction | null = null;
  actionTargetId = '';
  actionReason = '';
  editingStatus = '';

  isEditMode(): boolean {
    return Boolean(this.editingPublicationId());
  }

  dialogTitle(): string {
    return this.isEditMode()
      ? this.t('catalog.storefront.publishing.editTitle')
      : this.t('catalog.storefront.publishing.create');
  }

  dialogSaveLabel(): string {
    return this.isEditMode() ? this.t('catalog.action.save') : this.t('catalog.action.create');
  }

  ngOnInit(): void {
    this.search.set(this.route.snapshot.queryParamMap.get('q') ?? '');
    this.loading.set(false);
    this.service.listOfferings().subscribe({ next: items => this.offerings.set(items) });
    this.service.listMarketplaceStores().subscribe({ next: items => this.stores.set(items) });
    void this.startLiveUpdates();
  }

  ngOnDestroy(): void {
    void unsubscribeMarketplaceOperationsLiveUpdates(this.liveUpdateService, this.liveSubscription);
  }

  load(): void {
    if (!this.selectedStoreId) {
      this.loading.set(false);
      this.rows.set([]);
      return;
    }
    this.loading.set(true);
    this.service.listStorefrontPublications(this.selectedStoreId).subscribe({
      next: rows => {
        this.rows.set(rows);
        this.info.set('');
        this.loading.set(false);
      },
      error: () => {
        this.rows.set([]);
        this.loading.set(false);
      }
    });
  }

  onStoreChange(): void {
    this.selectedRows.set([]);
    this.load();
  }

  filteredRows(): StorefrontPublication[] {
    const value = this.search().trim().toLowerCase();
    const filtered = this.rows()
      .filter(row => !this.statusFilter || row.status === this.statusFilter)
      .filter(row => {
        if (!value) return true;
        const offering = this.offeringLabel(row.offeringId).toLowerCase();
        return offering.includes(value) || row.status.toLowerCase().includes(value);
      });

    const field = this.sortField();
    const order = this.sortOrder();
    return [...filtered].sort((left, right) => {
      if (field === 'offeringId') {
        return compareSortValues(this.offeringLabel(left.offeringId), this.offeringLabel(right.offeringId)) * order;
      }
      return compareSortValues(left[field], right[field]) * order;
    });
  }

  onSearch(event: Event): void {
    this.search.set((event.target as HTMLInputElement).value ?? '');
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { q: this.search().trim() || null },
      queryParamsHandling: 'merge'
    });
  }

  onSelectionChange(selection: StorefrontPublication[] | StorefrontPublication | null): void {
    this.selectedRows.set(Array.isArray(selection) ? selection : selection ? [selection] : []);
  }

  sortBy(field: PublicationSortField): void {
    toggleSortField(
      this.sortField(),
      field,
      this.sortOrder(),
      value => this.sortField.set(value),
      value => this.sortOrder.set(value)
    );
  }

  openSelected(): void {
    const row = this.selectedRows()[0];
    if (!row) return;
    this.openEdit(row);
  }

  openEdit(row: StorefrontPublication): void {
    this.editingPublicationId.set(row.id);
    this.editingStatus = row.status;
    this.selectedOfferingId = row.offeringId;
    this.displayOrder = row.displayOrder;
    this.visibleFromUtc = row.visibleFromUtc;
    this.visibleToUtc = row.visibleToUtc;
    this.dialogStoreId = row.storeId;
    this.badgePayloads = parsePublicationBadgesJson(row.badgesJson);
    this.seoOverrideJson = normalizePublicationSeoJson(row.seoOverrideJson);
    this.isPlatformDirectSaleAllowed = row.isPlatformDirectSaleAllowed;
    this.dialogError.set('');
    this.dialogVisible = true;
    this.loadOfferingAttributeSummary(row.offeringId);
  }

  applyBulk(): void {
    if (this.selectedRows().length === 0) {
      this.info.set(this.t('catalog.marketplace.actions.bulkNothingSelected'));
      return;
    }
    this.info.set(this.t('catalog.marketplace.actions.bulkQueued'));
  }

  exportRows(): void {
    const rows = this.filteredRows();
    if (rows.length === 0) {
      this.info.set(this.t('catalog.marketplace.actions.exportEmpty'));
      return;
    }

    downloadCsv(
      'storefront-publications.csv',
      ['Offering', 'Status', 'VisibleFromUtc', 'VisibleToUtc', 'DisplayOrder'],
      rows.map(row => [
        this.offeringLabel(row.offeringId),
        this.t(this.statusKey(row.status)),
        row.visibleFromUtc ?? '',
        row.visibleToUtc ?? '',
        row.displayOrder.toString()
      ]));
    this.info.set(this.t('catalog.marketplace.actions.exportReady'));
  }

  openDialog(): void {
    this.editingPublicationId.set(null);
    this.editingStatus = '';
    this.dialogStoreId = this.selectedStoreId;
    this.selectedOfferingId = null;
    this.offeringAttributes.set([]);
    this.displayOrder = (this.rows().length + 1) * 10;
    this.visibleFromUtc = null;
    this.visibleToUtc = null;
    this.badgePayloads = [];
    this.seoOverrideJson = '{}';
    this.isPlatformDirectSaleAllowed = false;
    this.dialogError.set('');
    this.dialogVisible = true;
  }

  onOfferingSelectionChange(offeringId: string | null): void {
    if (!offeringId?.trim()) {
      this.offeringAttributes.set([]);
      return;
    }

    this.loadOfferingAttributeSummary(offeringId);
  }

  loadOfferingAttributeSummary(offeringId: string): void {
    this.attributesLoading.set(true);
    this.service.getOfferingAttributes(offeringId).subscribe({
      next: items => {
        this.offeringAttributes.set(items);
        this.attributesLoading.set(false);
      },
      error: () => {
        this.offeringAttributes.set([]);
        this.attributesLoading.set(false);
      }
    });
  }

  isTruthyAttribute(item: OfferingAttributeAssignment): boolean {
    const value = item.value?.trim().toLowerCase() ?? '';
    return value === 'true' || value === '1';
  }

  formatAttributeSummaryValue(item: OfferingAttributeAssignment): string {
    if (item.type.toLowerCase() === 'boolean') {
      return this.isTruthyAttribute(item)
        ? this.t('catalog.offerings.attributes.value.true')
        : this.t('catalog.offerings.attributes.value.false');
    }

    return item.value?.trim() || '—';
  }

  closeDialog(): void {
    this.dialogVisible = false;
    this.editingPublicationId.set(null);
    this.editingStatus = '';
    this.offeringAttributes.set([]);
    this.dialogError.set('');
  }

  addBadge(): void {
    this.badgePayloads = [...this.badgePayloads, createEmptyPublicationBadgePayload()];
  }

  removeBadge(index: number): void {
    this.badgePayloads = this.badgePayloads.filter((_, itemIndex) => itemIndex !== index);
  }

  save(): void {
    this.dialogError.set('');
    if (!this.dialogStoreId?.trim()) {
      this.dialogError.set(this.t('catalog.storefront.publishing.validation.storeRequired'));
      return;
    }
    if (!this.selectedOfferingId?.trim()) {
      this.dialogError.set(this.t('catalog.storefront.publishing.validation.offeringRequired'));
      return;
    }

    this.acting.set(true);
    this.service.upsertStorefrontPublication({
      storeId: this.dialogStoreId.trim(),
      offeringId: this.selectedOfferingId.trim(),
      categoryId: null,
      visibleFromUtc: this.visibleFromUtc || null,
      visibleToUtc: this.visibleToUtc || null,
      displayOrder: this.displayOrder,
      badgesJson: serializePublicationBadgesJson(this.badgePayloads),
      seoOverrideJson: this.seoOverrideJson.trim() || '{}',
      merchandisingTagsJson: '[]',
      isPlatformDirectSaleAllowed: this.isPlatformDirectSaleAllowed
    }).subscribe({
      next: () => {
        this.selectedStoreId = this.dialogStoreId;
        this.closeDialog();
        this.load();
        this.acting.set(false);
      },
      error: () => {
        this.dialogError.set(this.t('catalog.storefront.publishing.validation.saveFailed'));
        this.acting.set(false);
      }
    });
  }

  openActionDialog(action: PublicationAction, id: string): void {
    this.actionType = action;
    this.actionTargetId = id;
    this.actionReason = '';
    this.actionError.set('');
    this.actionDialogVisible = true;
  }

  closeActionDialog(): void {
    this.actionDialogVisible = false;
    this.actionType = null;
    this.actionTargetId = '';
    this.actionReason = '';
    this.actionError.set('');
  }

  confirmAction(): void {
    if (!this.actionReason.trim()) {
      this.actionError.set(this.t('catalog.marketplace.storefront.confirm.reasonRequired'));
      return;
    }

    const id = this.actionTargetId;
    const action = this.actionType;
    if (!id || !action) return;

    this.acting.set(true);
    const request = action === 'publish'
      ? this.service.publishStorefrontPublication(id)
      : action === 'unpublish'
        ? this.service.unpublishStorefrontPublication(id)
        : this.service.archiveStorefrontPublication(id);

    request.subscribe({
      next: () => {
        this.closeActionDialog();
        this.load();
        this.acting.set(false);
      },
      error: error => {
        this.actionError.set(this.resolvePublishError(error));
        this.acting.set(false);
      }
    });
  }

  private resolvePublishError(error: unknown): string {
    const details = (error as { error?: { details?: { error?: string } } })?.error?.details?.error;
    if (details === 'active_approved_seller_offer_required') {
      return this.t('catalog.storefront.publishing.validation.sellerOfferRequired');
    }
    return this.t('catalog.storefront.publishing.validation.publishFailed');
  }

  actionDialogTitle(): string {
    switch (this.actionType) {
      case 'publish':
        return this.t('catalog.marketplace.storefront.confirm.publishTitle');
      case 'unpublish':
        return this.t('catalog.storefront.publishing.unpublish');
      case 'archive':
        return this.t('catalog.marketplace.storefront.confirm.archiveTitle');
      default:
        return '';
    }
  }

  actionConfirmLabel(): string {
    switch (this.actionType) {
      case 'publish':
        return this.t('catalog.storefront.publishing.publish');
      case 'unpublish':
        return this.t('catalog.storefront.publishing.unpublish');
      case 'archive':
        return this.t('catalog.storefront.publishing.archive');
      default:
        return '';
    }
  }

  offeringLabel(offeringId: string): string {
    const offering = this.offerings().find(item => item.id === offeringId);
    return offering ? `${offering.code} - ${offering.title}` : offeringId;
  }

  storeLabel(storeId: string): string {
    const store = this.stores().find(item => item.id === storeId);
    return store ? `${store.storeKey} - ${store.name}` : storeId;
  }

  statusKey(status: string): string {
    const map: Record<string, string> = {
      Draft: 'catalog.marketplace.storefront.status.draft',
      ReadyForReview: 'catalog.marketplace.storefront.status.draft',
      Published: 'catalog.marketplace.storefront.status.published',
      Hidden: 'catalog.marketplace.storefront.status.unpublished',
      Archived: 'catalog.marketplace.storefront.status.archived',
      Unpublished: 'catalog.marketplace.storefront.status.unpublished'
    };
    return map[status] ?? status;
  }

  private async startLiveUpdates(): Promise<void> {
    const tenantId = this.currentContext.context()?.tenantId;
    this.liveSubscription = await subscribeMarketplaceOperationsLiveUpdates(
      tenantId,
      this.liveUpdateService,
      () => this.load());
  }

  t(key: string): string { return this.localization.translate(key); }
}
